/*
 * Copyright (c) 2023. CodeGen International (Pvt) Ltd. All Rights Reserved.
 *
 * This software is the confidential and proprietary information of CodeGen
 * International (Pvt) Ltd. ("Confidential Information"). You shall not disclose
 * such Confidential Information and shall use it only in accordance with the
 * terms of the license agreement you entered into with CodeGen International.
 *
 */

/**
 * @author lakshan
 * @since 04 Jan, 2023
 */

import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {TCO} from '../../constants';
import {ConfigLoader} from '@tc-core/util/framework';
import {TranslateService} from '@ngx-translate/core';
import {
    GridColumnDefinitionProcessorService
} from '../../services/util/pre-processors/grid-column-definition-processor.service';
import {
    TemplateRendererComponent
} from '../../widgets/framework/ag-grid-custom-components/renderers/template-renderer/template-renderer.component';
import {
    CheckBoxRendererEditorComponent
} from '../../widgets/framework/ag-grid-custom-components/renderers/check-box-selector-renderer/check-box-renderer-editor.component';
import {ICellRendererParams} from 'ag-grid-community';
import {Sorter} from '@tc-core/model/it/codegen/ui/framework/sorter';
import {SorterService} from '@tc-core/service/sorters/sorter.service';
import {of, Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';

@Component({
    selector: 'tc-user-roles-editor',
    templateUrl: './user-roles-editor.component.html'
})
export class UserRolesEditorComponent implements OnInit, OnDestroy {

    onLangChangeSubscription: Subscription = new Subscription();

    constructor(
        private translateService: TranslateService,
        private configLoader: ConfigLoader,
        private columnDefinitionProcessorService: GridColumnDefinitionProcessorService,
        public sorterService: SorterService,
    ) {
        this.onLangChangeSubscription = this.translateService.onLangChange.subscribe(() => {
            this.gridApi.refreshHeader();
        });
    }

    @Input() gridHeaderConfig: any = TCO.CONF.CONF_USER_ROLES_GRID;
    @Input() rowData: any = [{code: '001', description: 'One'}, {code: '002', description: 'Two'}];
    @Input() leftHeading = ' 0 ' + 'Selected';
    @Input() rightHeading = this.rowData.length + ' ' + 'Results';
    @Input() sorters: Sorter[];
    @Input() rowSelection: any = 'multiple';
    @Input() domLayout: any = 'autoHeight';
    @Input() searchLabel: any;
    @Input() searchPlaceholder: any = 'Type here to search';
    @Input() searchIcon: any = 'search';
    @Input() buttonOneLabel: any = 'CANCEL';
    @Input() buttonTwoLabel: any = 'OK';
    @Input() ButtonOneEnable = true;
    @Input() ButtonTwoEnable = true;

    @Output() newRowSelectEvent = new EventEmitter<any>();
    @Output() onClickButtonOne = new EventEmitter<any>();
    @Output() onClickButtonTwo = new EventEmitter<any>();

    EMPTY_STRING = '';
    columnDefs;
    gridApi;
    gridOptions: any = {};
    filteredRowData = this.rowData;

    frameworkComponents: any = {
        templateRenderer: TemplateRendererComponent,
        checkBoxRendererEditor: CheckBoxRendererEditorComponent
    };
    defaultColDef = {
        resizable: true,
        headerValueGetter: (parameters: ICellRendererParams): string => {
            const headerIdentifier = parameters.colDef.headerName;
            if (headerIdentifier) {
                return this.translateService.instant(headerIdentifier);
            }
            return '';
        }
    };

    ngOnInit() {
        this.columnDefs = this.columnDefinitionProcessorService
            .createColumnDefs(this.configLoader.configurations.get(this.gridHeaderConfig), this);
    }

    public onGridReady(params) {
        this.gridApi = params.api;
        this.gridApi.sizeColumnsToFit();
    }

    public onFirstDataRendered(params) {
        this.gridApi = params.api;
        this.gridApi.sizeColumnsToFit();
    }

    public onRowSelected(params) {
        console.log(params);
        this.newRowSelectEvent.emit(params);
    }

    public onSort(event) {
        this.sorterService.sorter = event;
    }

    onClickBtnOne($event: MouseEvent) {
        this.onClickButtonOne.emit($event);
    }

    onClickBtnTwo($event: MouseEvent) {
        this.onClickButtonTwo.emit($event);
    }

    onSearch(value: any) {
        if (value) {
            this.filteredRowData = this.rowData.filter(data => data.code === value || data.description === value);
        } else {
            this.filteredRowData = this.rowData;
        }
    }

    ngOnDestroy(): void {
        if (this.onLangChangeSubscription) {
            this.onLangChangeSubscription.unsubscribe();
        }
    }
}
